import React, {
	useContext, useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import { VideoCacheContext } from '../../contexts/media/video-queue';
import Socket from '../../connection/socket';
import useShotMemos from '../../hooks/use-shot-memos';
import { GreenroomState } from '../../stores/types/types';
import SpotlightConnector from './spotlight-connector';
import { connectorIdLive } from './spotlight-utils';
import MediaSelector from '../main-video/media-selector';
import { propMap } from '../general-components/dynamic-component';
import useCanvasConfig from '../../hooks/use-canvas-config';

interface SpotlightManagerProps {
  socket: Socket | null;
  iterateLoadedMediaCount?: Function;
}

const defaultSpotlightProps = (key: number | string, value: number) => {
	const uniqueSpotlightId = connectorIdLive(key, value);
	return {
		uniqueSpotlightId,
		id: `media-video-${key}`,
		key: `${'key_FeedMedia_'}${key}`,
	};
};

const SpotlightManager: React.FC<SpotlightManagerProps> = ({
	socket,
	iterateLoadedMediaCount,
}) => {
	const media = useSelector((state: GreenroomState) => state.MediaReducer.media);
	const { currentShot } = useShotMemos();
	const { shotFeeds } = useCanvasConfig(currentShot);

	const { preloadedElements } = useContext(VideoCacheContext);

	const audioOnlyFeeds = useMemo(() => (shotFeeds?.length > 0
		? shotFeeds.filter((shotFeed) => shotFeed.audioOnly)
		: []), [shotFeeds]);

	const Spotlights = useMemo(() => {
		const otherSpotlights:any = {};
		preloadedElements?.current?.get('next-shot')?.forEach((elm, key) => {
			const { videoIndices: arrayOfPages, type, position } = elm;

			const isAudioOnly = audioOnlyFeeds.some((feed:any) => feed.media === key);
			arrayOfPages.forEach((value) => {
				if (value) {
					otherSpotlights[`${key}-${value}`] = {
						...defaultSpotlightProps(key, value),
						...(propMap[type](key, position, isAudioOnly, true, iterateLoadedMediaCount, socket)),
						type,
					};
				}
			});
		});

		preloadedElements?.current?.get('current-shot')?.forEach((elm, key) => {
			const { videoIndices: arrayOfPages, type, position } = elm;
			const isAudioOnly = audioOnlyFeeds.some((feed:any) => feed.media === key);
			arrayOfPages.forEach((value) => {
				if (value) {
					if (otherSpotlights[`${key}-${value}`]) {
						otherSpotlights[`${key}-${value}`] = {
							...otherSpotlights[`${key}-${value}`],
							...(propMap[type](
								key,
								position,
								isAudioOnly,
								false,
								iterateLoadedMediaCount,
								socket,
							)),
						};
					} else {
						otherSpotlights[`${key}-${value}`] = {
							...defaultSpotlightProps(key, value),
							...(propMap[type](
								key,
								position,
								isAudioOnly,
								false,
								iterateLoadedMediaCount,
								socket,
							)),
							type,
						};
					}
				}
			});
		});

		return <>{Object.keys(otherSpotlights).map((value) => (
			<SpotlightConnector
				key={otherSpotlights[value].key}
				uniqueSpotlightId={otherSpotlights[value].uniqueSpotlightId}
				mediaId={otherSpotlights[value].mediaId}
			>
				<MediaSelector
					key={`ms-${otherSpotlights[value].key}`}
					{...otherSpotlights[value]}
				/>
			</SpotlightConnector>
		))}</>;
	}, [
		preloadedElements?.current,
		media,
		audioOnlyFeeds,
		iterateLoadedMediaCount,
		socket,
		currentShot.shot_id,
	]);

	return (<>{Spotlights}</>);
};

export default SpotlightManager;
